import moment from 'moment';
import { Expansion, ExpansionLevelCap } from '../../../shared/gameConfig/expansion';
import { gameConfig } from './gameConfig';
import { Expansions } from '../../../shared/common/enums';
import { RaidDifficulty } from '../../../shared/gameConfig/sharedTypes';

export { Expansions, Patches, LoggedEntityStatus } from '../../../shared/common/enums';

// events between these next two numbers are considered to be periods.
// we do not include these in the overall event list, but fake their
// existence, since they are dynamic.
export const DYNAMIC_PERIOD_EVENT_START = 90000;
export const DYNAMIC_PERIOD_EVENT_END = 99999;

export const DEFAULT_CLASSIC_POINT_ALLOCATION = '0/0/0';

export const MIN_EXPANSION_ITEM_LEVEL = 10;

export const NAME_CHANGE_COOLDOWN_DAYS = 30;

export const SHOW_PATRON_LABEL_ABOVE_LEVEL = 2;

export const FAR_FUTURE_DATE = moment.utc('2030-01-01T00:00:00Z');

//
// Dragonflight Dates
//
export const DF_LAUNCH_DATE = moment.utc('2022-11-28T23:00:00Z');

export type TRoleResource = {
  slug: 'tank' | 'healer' | 'dps' | 'dps-melee' | 'dps-ranged';
  name: string;
};

export enum RealmConnectionType {
  LIVE = 'live',
  PTR = 'ptr',
  BETA = 'beta',
  TR = 'tr'
}

export const ROLE_LIST: TRoleResource[] = [
  {
    slug: 'tank',
    name: 'Tank'
  },
  {
    slug: 'healer',
    name: 'Healer'
  },
  {
    slug: 'dps',
    name: 'DPS'
  },
  {
    slug: 'dps-ranged',
    name: 'Ranged DPS'
  },
  {
    slug: 'dps-melee',
    name: 'Melee DPS'
  }
];

export const ROLE_LIST_BY_STRING = ROLE_LIST.reduce((accum: any, item: any) => {
  accum[item.slug] = item;
  return accum;
}, {});

export const ROLE_LABELS: Record<string, string> = {
  tank: 'common:roles.tank.name',
  healer: 'common:roles.healer.name',
  dps: 'common:roles.dps.name',
  'dps-ranged': 'common:roles.ranged-dps.name',
  'dps-melee': 'common:roles.melee-dps.name'
};

export enum ROLE_IDS {
  NONE = 0,
  TANK = 1,
  HEALER = 2,
  DPS = 3,
  DPS_MELEE = 4,
  DPS_RANGED = 5
}

export const ROLE_ID_TO_STRING: any = {
  [ROLE_IDS.TANK]: 'tank',
  [ROLE_IDS.HEALER]: 'healer',
  [ROLE_IDS.DPS]: 'dps',
  [ROLE_IDS.DPS_MELEE]: 'dps-melee',
  [ROLE_IDS.DPS_RANGED]: 'dps-ranged'
};

export const ROLE_STRING_TO_ID: any = {
  tank: ROLE_IDS.TANK,
  healer: ROLE_IDS.HEALER,
  dps: ROLE_IDS.DPS,
  'dps-melee': ROLE_IDS.DPS_MELEE,
  'dps-ranged': ROLE_IDS.DPS_RANGED
};

export type TRaidDifficultyResource = {
  slug: string;
  name: string;
  abbrev: string;
};

export const RAID_DIFFICULTY_LIST: TRaidDifficultyResource[] = [
  {
    slug: 'normal',
    name: 'Normal',
    abbrev: 'N'
  },
  {
    slug: 'heroic',
    name: 'Heroic',
    abbrev: 'H'
  },
  {
    slug: 'mythic',
    name: 'Mythic',
    abbrev: 'M'
  }
];

export const LevelCap = {
  ...ExpansionLevelCap,
  Current: gameConfig.CurrentLevelCap,
  Previous: gameConfig.PreviousLevelCap,
  MinScanLevel: gameConfig.MinScanLevel
};

export type TExpansionInfo = {
  slug: string;
  expansionId: Expansion;
  name: string;
  short_name: string;
  icon?: string;
};

export const EXPANSIONS_INFO: TExpansionInfo[] = [
  {
    slug: 'dragonflight',
    expansionId: Expansions.Dragonflight,
    name: 'Dragonflight',
    short_name: 'DF'
  },
  {
    slug: 'shadowlands',
    expansionId: Expansions.Shadowlands,
    name: 'Shadowlands',
    short_name: 'SL',
    icon: 'inv_misc_token_boost_shadowlands'
  },
  {
    slug: 'battle-for-azeroth',
    expansionId: Expansions.BattleForAzeroth,
    name: 'Battle for Azeroth',
    short_name: 'BFA',
    icon: 'inv_misc_token_boost_bfa'
  },
  {
    slug: 'legion',
    expansionId: Expansions.Legion,
    name: 'Legion',
    short_name: 'Legion',
    icon: 'inv_misc_token_boost_generic'
  },
  {
    slug: 'mists-of-pandaria',
    expansionId: Expansions.MistsOfPandaria,
    name: 'Mists of Pandaria',
    short_name: 'MISTS',
    icon: 'inv_misc_token_boost_generic'
  },
  {
    slug: 'cataclysm',
    expansionId: Expansions.Cataclysm,
    name: 'Cataclysm',
    short_name: 'CATA',
    icon: 'inv_misc_token_boost_generic'
  },
  {
    slug: 'wrath-of-the-lich-king',
    expansionId: Expansions.WrathOfTheLichKing,
    name: 'Wrath of the Lich King',
    short_name: 'WOTLK',
    icon: 'inv_misc_token_boost_generic'
  }
];

export const EXPANSIONS_INFO_BY_EXPANSION_ID = new Map<number, TExpansionInfo>(
  EXPANSIONS_INFO.map((xpacInfo: TExpansionInfo) => [xpacInfo.expansionId, xpacInfo])
);

// FIXME: make this get sent up from the server
export const FACTIONS = [
  {
    name: 'Alliance',
    slug: 'alliance'
  },
  {
    name: 'Horde',
    slug: 'horde'
  }
];

export const FACTIONS_WITH_MIXED = [
  {
    name: 'Alliance',
    slug: 'alliance'
  },
  {
    name: 'Horde',
    slug: 'horde'
  },
  {
    name: 'Mixed Factions',
    slug: 'mixed'
  }
];

export const Difficulty = RaidDifficulty;

export const DIFFICULTIES = gameConfig.RaidDifficulties;

export const DIFFICULTY_SLUGS = DIFFICULTIES.map((d) => d.slug);
export const DIFFICULTY_SLUGS_DESCENDING = [...DIFFICULTY_SLUGS].reverse();
export const DIFFICULTIES_BY_SLUG = DIFFICULTIES.reduce((acc: any, item) => {
  acc[item.slug] = item;
  return acc;
}, {} as any);
export const DIFFICULTIES_BY_NAME = DIFFICULTIES.reduce((acc: any, item) => {
  acc[item.name] = item;
  return acc;
}, {} as any);

export enum GroupCharterLimits {
  Default = 1,
  Patron_Revered = 2,
  Patron_Exalted = 3,
  Patron_Illustrious = 4
}

export type TRaidKillCountsByDifficulty = {
  normal10: number;
  normal25: number;
  heroic10: number;
  heroic25: number;
  [key: string]: number;
};

// mapId comes from: https://wow.tools/dbc/?dbc=map (is mapId even used here? We should use what's in db i guess?)
export const RAID_METADATA = gameConfig.Raids;
export const RAID_METADATA_BY_SLUG = gameConfig.RaidsBySlug;

// list of all the widgets that support version updates from backend.
export enum WidgetNames {
  BossProgress = 'boss-progress',
  RaidLeaderboard = 'raid-leaderboard',
  HealthOverAttempt = 'health-over-attempt',
  GuildGearCompare = 'guild-gear-compare',
  Versus = 'versus',
  GuildGearComposition = 'guild-gear-composition',
  TeamDungeons = 'team-dungeons'
}

export type TPatronPremium = 'premium_5' | 'premium_4' | 'premium_3' | 'premium_2' | 'premium_1';

export type TPremiumList = {
  premium_5_count: number;
  premium_4_count: number;
  premium_3_count: number;
  premium_2_count: number;
  premium_1_count: number;
  [key: string]: number;
};

export enum SpecialTags {
  WeeklyRoute = 'weekly-route',
  PlayLikeThePros = 'play-like-the-pros'
}

export enum ArticleParticipantCategory {
  CO_AUTHOR = 'co_author',
  SPOTLIGHT = 'spotlight'
}

export enum UserStarredReplaysLimit {
  DEFAULT = 15,
  PATRON = 30
}

export enum UserReplayAttemptStatus {
  VALID = 'valid',
  INVALID = 'invalid',
  PENDING = 'pending',
  DEFAULT = PENDING
}

export { RealmTypeFlags } from '../../../shared/gameConfig/sharedTypes';

export enum EWarcraftClass {
  Warrior = 0,
  Paladin,
  Hunter,
  Rogue,
  Priest,
  DeathKnight,
  Shaman,
  Mage,
  Warlock,
  Monk,
  Druid,
  MAX_CLASSES
}

export const SEASONS = gameConfig.MythicPlusSeasons;
export const SEASONS_SORTED = gameConfig.MythicPlusSeasonsSorted;
export const SEASONS_BY_SLUG = gameConfig.MythicPlusSeasonsBySlug;
export const SEASONS_BY_PARTITION_EVENT_ID = gameConfig.MythicPlusSeasonsByPartitionEventId;
